import { useEffect, useState, type CSSProperties } from 'react'
import { Minus, Plus } from 'lucide-react'
import { formatRevenueCompact, formatRevenueFull } from './formatters'
import { parseMoney } from '@/components/Plan/MoneyInput'
import type { PlanNode } from './PlanNodesPanel'

type TargetChangeHandler = (id: string, target: number | null, growthPctOverride?: number | null) => void

// The Growth % cell: a percent input that writes a target revenue back to
// the parent, or a plain read-only value when the row can't be edited.
export function GrowthInput({
  node,
  baseRevenue,
  actualRevenue,
  readOnly,
  cellClass,
  cellStyle,
  onTargetChange
}: {
  node: PlanNode
  baseRevenue: number
  // Months already sold on a quarterly revision (0 on a plain plan): growth
  // reads on the full year, so the target written is the year's number
  // minus these.
  actualRevenue: number
  readOnly: boolean
  cellClass: string
  cellStyle: CSSProperties
  onTargetChange: TargetChangeHandler
}) {
  const [growthInput, setGrowthInput] = useState<string>(
    node.growthPct == null ? '' : String(node.growthPct)
  )

  useEffect(() => {
    const localNum = growthInput === '' ? null : Number(growthInput)
    if (localNum !== node.growthPct) {
      setGrowthInput(node.growthPct == null ? '' : String(node.growthPct))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.growthPct])

  const handleGrowthChange = (raw: string) => {
    setGrowthInput(raw)
    if (raw === '') {
      onTargetChange(node.id, null, null)
      return
    }
    if (!/^-?\d*\.?\d*$/.test(raw)) return
    if (raw === '-' || raw === '.' || raw === '-.') return
    const g = Number(raw)
    if (!Number.isFinite(g)) return
    const target = Math.max(0, Math.round(baseRevenue * (1 + g / 100) - actualRevenue))
    onTargetChange(node.id, target, g)
  }

  return (
    <div className={`border-b py-2 px-3 flex items-center justify-end${cellClass}`} style={cellStyle}>
      {readOnly ? (
        <span className="text-xs tabular-nums" style={{ color: 'var(--foreground)' }}>
          {node.growthPct == null ? '—' : `${node.growthPct}%`}
        </span>
      ) : (
        <div className="relative" style={{ width: 85 }}>
          <input
            type="text"
            inputMode="decimal"
            value={growthInput}
            onChange={(e) => handleGrowthChange(e.target.value)}
            placeholder="0"
            style={{
              background: 'var(--background)',
              borderColor: 'var(--border)',
              color: 'var(--foreground)'
            }}
            className="w-full text-right text-xs tabular-nums border rounded-md pl-1.5 pr-5 py-1 outline-none transition-colors hover:border-border focus:border-secondary"
          />
          <span
            className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs"
            style={{ color: 'var(--muted-foreground)' }}
          >
            %
          </span>
        </div>
      )}
    </div>
  )
}

// The Target Revenue cell: a money input plus the fill-remaining /
// deduct-excess shortcut buttons, or a plain read-only value.
export function TargetInput({
  node,
  baseRevenue,
  targetValue,
  fullYear = null,
  currency,
  readOnly,
  fillableRest,
  deductibleRest,
  cellClass,
  cellStyle,
  onTargetChange
}: {
  node: PlanNode
  baseRevenue: number
  targetValue: number
  // Quarterly revision: the year's number (months already sold + this
  // target), shown as a muted line under the input. Null on a plain plan.
  fullYear?: number | null
  currency?: string | null
  readOnly: boolean
  fillableRest: number
  deductibleRest: number
  cellClass: string
  cellStyle: CSSProperties
  onTargetChange: TargetChangeHandler
}) {
  const [targetFocused, setTargetFocused] = useState(false)
  const [targetDraft, setTargetDraft] = useState('')

  const handleTargetChange = (raw: string) => {
    if (raw === '') {
      onTargetChange(node.id, null)
      return
    }
    const parsed = Number(raw)
    if (!Number.isFinite(parsed)) return
    onTargetChange(node.id, Math.max(0, parsed))
  }

  return (
    <div className={`border-b py-2 px-3 flex flex-col items-end justify-center${cellClass}`} style={cellStyle}>
      <div className="flex items-center justify-end gap-1">
      {readOnly ? (
        <span
          className="text-xs tabular-nums"
          style={{ color: 'var(--foreground)' }}
          title={formatRevenueFull(targetValue, currency)}
        >
          {formatRevenueFull(targetValue, currency)}
        </span>
      ) : (
        <input
          type="text"
          inputMode="text"
          value={
            targetFocused
              ? targetDraft
              : formatRevenueFull(String(node.targetRevenue ?? baseRevenue), currency)
          }
          onFocus={(e) => {
            const current = node.targetRevenue ?? baseRevenue
            setTargetDraft(current === 0 ? '' : formatRevenueFull(current, currency))
            setTargetFocused(true)
            setTimeout(() => e.target.select(), 0)
          }}
          onBlur={() => {
            setTargetFocused(false)
            const trimmed = targetDraft.trim()
            if (trimmed === '') {
              handleTargetChange('0')
              return
            }
            const parsed = parseMoney(trimmed, currency)
            if (parsed == null) return
            handleTargetChange(String(parsed))
          }}
          onChange={(e) => setTargetDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              ;(e.target as HTMLInputElement).blur()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              setTargetFocused(false)
            }
          }}
          placeholder="0"
          style={{
            background: 'var(--background)',
            borderColor: 'var(--border)',
            color: 'var(--foreground)',
            width: 140
          }}
          className="text-right text-xs tabular-nums border rounded-md px-1.5 py-1 outline-none transition-colors hover:border-border focus:border-secondary"
        />
      )}
      <div className="shrink-0" style={{ width: 18, height: 18 }}>
        {!readOnly && fillableRest > 0 && (
          <button
            type="button"
            onClick={() => {
              const next = (node.targetRevenue ?? baseRevenue) + fillableRest
              onTargetChange(node.id, Math.max(0, next))
            }}
            title={`Fill remaining ${formatRevenueFull(fillableRest, currency)}`}
            aria-label="Fill remaining"
            className="inline-flex items-center justify-center rounded transition-colors hover:bg-accent w-full h-full"
            style={{ color: 'var(--muted-foreground)' }}
          >
            <Plus size={14} strokeWidth={2} />
          </button>
        )}
        {!readOnly && deductibleRest > 0 && (
          <button
            type="button"
            onClick={() => {
              const next = (node.targetRevenue ?? baseRevenue) - deductibleRest
              onTargetChange(node.id, Math.max(0, next))
            }}
            title={`Deduct excess ${formatRevenueFull(deductibleRest, currency)}`}
            aria-label="Deduct excess"
            className="inline-flex items-center justify-center rounded transition-colors hover:bg-accent w-full h-full"
            style={{ color: 'var(--muted-foreground)' }}
          >
            <Minus size={14} strokeWidth={2} />
          </button>
        )}
      </div>
      </div>
      {fullYear != null && (
        <div className="flex items-center justify-end gap-1">
          <span
            className="text-xs tabular-nums"
            style={{ color: 'var(--muted-foreground)' }}
            title={`Full year (sold so far + this target): ${formatRevenueFull(fullYear, currency)}`}
          >
            {formatRevenueCompact(fullYear, currency)}
          </span>
          <div className="shrink-0" style={{ width: 18 }} aria-hidden />
        </div>
      )}
    </div>
  )
}
